import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { X, User, Package, DollarSign, Truck } from 'lucide-react';
import { useShipmentDetail } from '../hooks/usePlaneteCourrier';

interface ShipmentDetailPanelProps {
  shipmentNumber: string;
  onClose: () => void;
}

function AddressBlock({ label, address, contact }: {
  label: string;
  address: { name: string; streetAddress: string; suite: string | null; city: string; postalCode: string };
  contact: { name: string; phoneNumbers: { number: string }[]; email: string | null };
}) {
  return (
    <div className="space-y-1">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase">{label}</h4>
      <p className="text-sm font-medium">{address.name}</p>
      <p className="text-sm text-muted-foreground">
        {address.streetAddress}{address.suite ? `, ${address.suite}` : ''}
      </p>
      <p className="text-sm text-muted-foreground">{address.city}, {address.postalCode}</p>
      <div className="pt-1">
        <p className="text-sm"><User className="inline h-3 w-3 mr-1" />{contact.name}</p>
        {contact.phoneNumbers.map((p, i) => (
          <p key={i} className="text-xs text-muted-foreground">{p.number}</p>
        ))}
        {contact.email && <p className="text-xs text-muted-foreground">{contact.email}</p>}
      </div>
    </div>
  );
}

export function ShipmentDetailPanel({ shipmentNumber, onClose }: ShipmentDetailPanelProps) {
  const { data: shipment, isLoading } = useShipmentDetail(shipmentNumber);

  if (isLoading) {
    return (
      <div className="border-l bg-white p-6 w-[480px] shrink-0">
        <div className="text-center text-muted-foreground py-12">Loading shipment details...</div>
      </div>
    );
  }

  if (!shipment) return null;

  const subtotal = shipment.prices.reduce((sum, p) => sum + p.price, 0);
  const taxTotal = shipment.taxes.reduce((sum, t) => sum + t.amount, 0);
  const total = subtotal + taxTotal;

  return (
    <div className="border-l bg-white w-[480px] shrink-0 overflow-y-auto">
      <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
        <div>
          <h3 className="font-semibold text-lg">Shipment #{shipment.number}</h3>
          <Badge className="mt-1">{shipment.statusName}</Badge>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-muted rounded">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="p-4 space-y-6">
        {/* Service Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Service</p>
            <p className="text-sm font-medium flex items-center gap-1">
              <Truck className="h-3 w-3" /> {shipment.serviceName}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Shipment Date</p>
            <p className="text-sm font-medium">{format(new Date(shipment.shipmentDate), 'yyyy-MM-dd HH:mm')}</p>
          </div>
          {shipment.deliverByDateTime && (
            <div>
              <p className="text-xs text-muted-foreground">Deliver By</p>
              <p className="text-sm font-medium">{format(new Date(shipment.deliverByDateTime), 'yyyy-MM-dd HH:mm')}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-muted-foreground">Distance</p>
            <p className="text-sm font-medium">{shipment.distanceInKm.toFixed(0)} km</p>
          </div>
          {shipment.aliasReferences.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground">{shipment.aliasReferences[0].alias}</p>
              <p className="text-sm font-medium">{shipment.aliasReferences[0].value}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-muted-foreground">Carrier</p>
            <p className="text-sm font-medium">{shipment.deliveryCarrierName}</p>
          </div>
        </div>

        {/* Addresses */}
        <div className="grid grid-cols-2 gap-4 pt-2 border-t">
          <AddressBlock label="Origin" address={shipment.originAddress} contact={shipment.originContact} />
          <AddressBlock label="Destination" address={shipment.destinationAddress} contact={shipment.destinationContact} />
        </div>

        {/* Packages */}
        <div className="pt-2 border-t">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1">
            <Package className="h-3 w-3" /> Packages ({shipment.packages.length})
          </h4>
          {shipment.packages.map((pkg) => (
            <div key={pkg.id} className="bg-muted/50 rounded p-3 text-sm mb-2">
              <div className="flex justify-between">
                <span className="font-medium">{pkg.packageTypeName}</span>
                <span className="text-muted-foreground">{pkg.packageStatusName}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                PIN: {pkg.pin}
                {pkg.partnerPackageNumber && <> · Partner: {pkg.partnerPackageNumber}</>}
              </div>
              <div className="text-xs text-muted-foreground">
                {pkg.weight} lbs · {pkg.length}×{pkg.width}×{pkg.height} in
              </div>
            </div>
          ))}
        </div>

        {/* Pricing */}
        <div className="pt-2 border-t">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1">
            <DollarSign className="h-3 w-3" /> Pricing
          </h4>
          <div className="space-y-1">
            {shipment.prices.filter(p => p.price > 0).map((p) => (
              <div key={p.id} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{p.priceTypeName}</span>
                <span>${p.price.toFixed(2)}</span>
              </div>
            ))}
            <div className="border-t pt-1 mt-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {shipment.taxes.map((t) => (
                <div key={t.id} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t.taxTypeName} ({t.percentageRate}%)</span>
                  <span>${t.amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-semibold pt-1 border-t">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
